// src/pages/AgencyEnrollmentTracker.jsx
// Agency-wide Enrollment Tracker — 5-tab dashboard
// Tabs: Command Center | Pipeline Kanban | Commission Forecast | T65 Pipeline | Carrier Intelligence
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import {
  TrendingUp, TrendingDown, Users, Target, DollarSign, Activity,
  AlertCircle, CheckCircle, Clock, ChevronRight, Plus, X, ArrowLeft,
  ArrowRight, Settings, RefreshCw, FileText, Phone, Calendar,
  BarChart3, PieChart as PieChartIcon, Zap, Star, Filter, ChevronDown,
  Edit3, MessageSquare, Save
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ============================================================================
// DESIGN TOKENS
// ============================================================================
const CARRIER_COLORS = [
  '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444',
  '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1'
];

const STAGE_CONFIG = {
  lead:             { label: 'Lead',                   color: '#6b7280', bg: 'bg-gray-700' },
  quote_sent:       { label: 'Quote Sent',             color: '#3b82f6', bg: 'bg-blue-700' },
  soa_completed:    { label: 'SOA Completed',          color: '#8b5cf6', bg: 'bg-purple-700' },
  app_submitted:    { label: 'App Submitted',          color: '#f59e0b', bg: 'bg-amber-700' },
  pending_approval: { label: 'Pending Approval',       color: '#f97316', bg: 'bg-orange-700' },
  enrolled:         { label: 'Enrolled',               color: '#10b981', bg: 'bg-emerald-700' },
  declined:         { label: 'Declined / Cancelled',   color: '#ef4444', bg: 'bg-red-700' },
};
const STAGES = Object.keys(STAGE_CONFIG);

// ============================================================================
// SHARED SMALL COMPONENTS
// ============================================================================
function Card({ children, className = '' }) {
  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg ${className}`}>
      {children}
    </div>
  );
}

function KpiCard({ title, value, subtitle, icon: Icon, trend, loading, accent = false }) {
  return (
    <Card className="p-5">
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-3 bg-gray-800 rounded w-24" />
          <div className="h-8 bg-gray-800 rounded w-16" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400 uppercase tracking-wide">{title}</span>
            {Icon && <Icon size={16} className={accent ? 'text-amber-400' : 'text-gray-500'} />}
          </div>
          <div className="flex items-end gap-2">
            <span className={`text-3xl font-bold tabular-nums ${accent ? 'text-amber-400' : 'text-white'}`}>
              {value}
            </span>
            {trend != null && (
              <span className={`text-xs flex items-center mb-1 ${trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                {trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {Math.abs(trend)}%
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </>
      )}
    </Card>
  );
}

function SectionHeader({ children }) {
  return <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">{children}</h2>;
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw size={24} className="text-amber-400 animate-spin" />
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-gray-600">
      <BarChart3 size={32} className="mb-3" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

// ============================================================================
// ENROLLMENT PERIOD BANNER
// ============================================================================
function PeriodBanner({ status }) {
  if (!status) return null;
  const { type, label, active } = status;

  if (active) {
    return (
      <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold ${
        type === 'AEP'
          ? 'bg-amber-400/10 border border-amber-400/30 text-amber-300'
          : 'bg-blue-400/10 border border-blue-400/30 text-blue-300'
      }`}>
        <span className="relative flex h-2.5 w-2.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${type === 'AEP' ? 'bg-amber-400' : 'bg-blue-400'}`} />
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${type === 'AEP' ? 'bg-amber-400' : 'bg-blue-400'}`} />
        </span>
        {label}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-400 text-sm">
      <Clock size={14} />
      {label}
    </div>
  );
}

// ============================================================================
// TAB 1: COMMAND CENTER
// ============================================================================
function CommandCenter({ user }) {
  const [kpis, setKpis] = useState(null);
  const [velocity, setVelocity] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [goal, setGoal] = useState('');
  const [goalLoading, setGoalLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const isOwner = ['agency_owner', 'agency_admin', 'agency', 'fmo', 'admin', 'founder'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kRes, vRes, cRes, rRes] = await Promise.all([
        fetch(`${BASE}/api/enrollment-tracker/kpis`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/velocity`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/carrier-dist`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/recent`, { credentials: 'include' }),
      ]);
      const [kd, vd, cd, rd] = await Promise.all([kRes.json(), vRes.json(), cRes.json(), rRes.json()]);
      if (kd.ok) setKpis(kd.data);
      if (vd.ok) setVelocity(vd.data);
      if (cd.ok) setCarriers(cd.data);
      if (rd.ok) setRecent(rd.data);
      if (kd.ok && kd.data.goalTarget) setGoal(String(kd.data.goalTarget));
    } catch (e) {
      console.error('[CommandCenter] load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGoal = async () => {
    if (!goal || isNaN(parseInt(goal))) return;
    setGoalLoading(true);
    try {
      await fetch(`${BASE}/api/enrollment-tracker/goals`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          period_type: kpis?.periodStatus?.type?.toLowerCase() || 'aep',
          target_count: parseInt(goal),
          period_year: new Date().getFullYear()
        })
      });
      load();
    } catch (e) {
      console.error('[CommandCenter] saveGoal error', e);
    } finally {
      setGoalLoading(false);
    }
  };

  const maxCarrier = carriers.reduce((m, c) => Math.max(m, c.count), 0) || 1;
  const goalTarget = parseInt(goal) || kpis?.goalTarget;
  const enrolled = kpis?.enrollmentsThisPeriod || 0;
  const goalPct = goalTarget ? Math.min(Math.round((enrolled / goalTarget) * 100), 100) : null;

  return (
    <div className="space-y-6">
      {/* Period Banner + Goal */}
      <div className="flex flex-wrap items-center gap-3">
        <PeriodBanner status={kpis?.periodStatus} />
        {isOwner && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500">Enrollment Goal:</span>
            <input
              type="number"
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder="e.g. 150"
              className="w-20 px-2 py-1 text-sm bg-gray-800 border border-gray-700 rounded text-white tabular-nums"
            />
            <button
              onClick={saveGoal}
              disabled={goalLoading}
              className="px-3 py-1 text-xs bg-amber-400 text-gray-950 font-semibold rounded hover:bg-amber-300 disabled:opacity-50"
            >
              {goalLoading ? '...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Goal Progress */}
      {goalPct !== null && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Goal Progress</span>
            <span className="text-sm font-semibold tabular-nums text-amber-400">
              {enrolled} / {goalTarget} ({goalPct}%)
            </span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-2 rounded-full bg-amber-400 transition-all duration-500"
              style={{ width: `${goalPct}%` }}
            />
          </div>
        </Card>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard title="Enrollments This Period" value={kpis?.enrollmentsThisPeriod ?? '—'} icon={CheckCircle} accent loading={loading} />
        <KpiCard title="New Clients" value={kpis?.newClientsThisPeriod ?? '—'} icon={Users} loading={loading} />
        <KpiCard title="Plan Changes" value={kpis?.planChanges ?? '—'} icon={ArrowRight} loading={loading} />
        <KpiCard title="Active Agents" value={kpis?.activeAgents ?? '—'} icon={Activity} loading={loading} />
        <KpiCard
          title="Est. Commission"
          value={kpis?.estimatedCommission != null
            ? `$${(kpis.estimatedCommission / 1000).toFixed(0)}k`
            : '—'}
          icon={DollarSign}
          accent
          loading={loading}
        />
      </div>

      {/* Velocity Chart */}
      <Card className="p-5">
        <SectionHeader>Enrollment Velocity — Last 30 Days</SectionHeader>
        {loading ? <Spinner /> : velocity.length === 0 ? <EmptyState message="No enrollment data in the last 30 days" /> : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={velocity} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="velGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis
                dataKey="date"
                tickFormatter={d => d.slice(5)}
                tick={{ fill: '#6b7280', fontSize: 11 }}
                interval={4}
              />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 6 }}
                labelStyle={{ color: '#9ca3af' }}
                itemStyle={{ color: '#f59e0b' }}
              />
              {goalTarget && (
                <ReferenceLine
                  y={Math.round(goalTarget / 30)}
                  stroke="#f59e0b"
                  strokeDasharray="4 4"
                  label={{ value: 'Daily Pace', fill: '#f59e0b', fontSize: 10, position: 'right' }}
                />
              )}
              <Area
                type="monotone"
                dataKey="count"
                stroke="#f59e0b"
                strokeWidth={2}
                fill="url(#velGrad)"
                name="Enrollments"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Carrier Distribution */}
        <Card className="p-5">
          <SectionHeader>Carrier Distribution — This Period</SectionHeader>
          {loading ? <Spinner /> : carriers.length === 0 ? <EmptyState message="No carrier data" /> : (
            <div className="space-y-3">
              {carriers.map((c, i) => (
                <div key={c.carrier}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-300 truncate max-w-[60%]">{c.carrier}</span>
                    <span className="text-gray-400 tabular-nums">{c.count} ({c.pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: `${Math.round((c.count / maxCarrier) * 100)}%`,
                        backgroundColor: CARRIER_COLORS[i % CARRIER_COLORS.length]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Enrollments Feed */}
        <Card className="p-5">
          <SectionHeader>Recent Enrollments</SectionHeader>
          {loading ? <Spinner /> : recent.length === 0 ? <EmptyState message="No recent enrollments" /> : (
            <div className="space-y-2.5">
              {recent.map(r => (
                <div key={r.id} className="flex items-center gap-3 py-2 border-b border-gray-800 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-amber-400/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-amber-400">{r.clientMasked.slice(0, 2)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white truncate">{r.clientMasked}</span>
                      <span className="text-xs text-gray-500 tabular-nums shrink-0">
                        {r.effectiveDate ? new Date(r.effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="truncate">{r.carrier}</span>
                      <span>•</span>
                      <span>{r.planType}</span>
                      <span>•</span>
                      <span className="truncate">{r.agentName}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// TAB 2: PIPELINE KANBAN
// ============================================================================
function PipelineKanban({ user }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterCarrier, setFilterCarrier] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showAddModal, setShowAddModal] = useState(null); // stage key
  const [newNote, setNewNote] = useState('');
  const [slideHistory, setSlideHistory] = useState([]);
  const [slideLoading, setSlideLoading] = useState(false);
  const [addForm, setAddForm] = useState({ client_name: '', carrier: '', plan_type: '', plan_name: '', priority: 'normal' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAgent) params.set('agent_id', filterAgent);
      if (filterCarrier) params.set('carrier', filterCarrier);
      const res = await fetch(`${BASE}/api/enrollment-tracker/pipeline?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setCards(json.data);
    } catch (e) {
      console.error('[Kanban] load error', e);
    } finally {
      setLoading(false);
    }
  }, [filterAgent, filterCarrier]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async (cardId) => {
    setSlideLoading(true);
    try {
      const res = await fetch(`${BASE}/api/enrollment-tracker/pipeline/${cardId}/history`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setSlideHistory(json.data);
    } finally {
      setSlideLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCard) loadHistory(selectedCard.id);
  }, [selectedCard, loadHistory]);

  const advanceStage = async (card) => {
    const idx = STAGES.indexOf(card.stage);
    if (idx >= STAGES.length - 2) return; // don't advance past pending_approval automatically into enrolled/declined
    const nextStage = STAGES[idx + 1];
    await fetch(`${BASE}/api/enrollment-tracker/pipeline/${card.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage: nextStage })
    });
    load();
    if (selectedCard?.id === card.id) setSelectedCard({ ...selectedCard, stage: nextStage });
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedCard) return;
    await fetch(`${BASE}/api/enrollment-tracker/pipeline/${selectedCard.id}/notes`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: newNote.trim() })
    });
    setNewNote('');
    loadHistory(selectedCard.id);
  };

  const createCard = async () => {
    if (!addForm.client_name.trim()) return;
    await fetch(`${BASE}/api/enrollment-tracker/pipeline`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...addForm, stage: showAddModal })
    });
    setShowAddModal(null);
    setAddForm({ client_name: '', carrier: '', plan_type: '', plan_name: '', priority: 'normal' });
    load();
  };

  const setStage = async (cardId, stage) => {
    await fetch(`${BASE}/api/enrollment-tracker/pipeline/${cardId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage })
    });
    load();
    if (selectedCard?.id === cardId) setSelectedCard({ ...selectedCard, stage });
  };

  const filteredCards = cards.filter(c => {
    if (overdueOnly && c.days_in_stage < 14) return false;
    return true;
  });

  const byStage = {};
  STAGES.forEach(s => { byStage[s] = []; });
  filteredCards.forEach(c => {
    if (byStage[c.stage]) byStage[c.stage].push(c);
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          placeholder="Filter by carrier"
          value={filterCarrier}
          onChange={e => setFilterCarrier(e.target.value)}
          className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600 w-40"
        />
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={e => setOverdueOnly(e.target.checked)}
            className="accent-amber-400"
          />
          Overdue only (&gt;14 days)
        </label>
        <button onClick={load} className="ml-auto text-gray-500 hover:text-amber-400">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {STAGES.map(stage => {
            const cfg = STAGE_CONFIG[stage];
            const stageCards = byStage[stage];
            return (
              <div key={stage} className="w-56 shrink-0">
                {/* Column Header */}
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
                    <span className="text-xs font-semibold text-gray-300">{cfg.label}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600 tabular-nums">{stageCards.length}</span>
                    <button
                      onClick={() => setShowAddModal(stage)}
                      className="text-gray-600 hover:text-amber-400 transition-colors"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                {/* Cards */}
                <div className="space-y-2 min-h-[120px]">
                  {stageCards.map(card => {
                    const days = Math.floor(card.days_in_stage || 0);
                    const overdue = days >= 14;
                    return (
                      <div
                        key={card.id}
                        onClick={() => setSelectedCard(card)}
                        className={`bg-gray-900 border rounded-lg p-3 cursor-pointer hover:border-amber-400/50 transition-colors ${
                          overdue ? 'border-red-500/60' : 'border-gray-800'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-1 mb-1.5">
                          <span className="text-sm font-medium text-white leading-tight truncate">
                            {card.client_name || 'Unknown'}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 font-semibold ${
                            overdue ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-500'
                          }`}>
                            {days}d
                          </span>
                        </div>
                        {card.carrier && (
                          <p className="text-xs text-gray-500 truncate">{card.carrier}</p>
                        )}
                        {card.plan_type && (
                          <p className="text-xs text-gray-600 truncate">{card.plan_type}</p>
                        )}
                        <p className="text-xs text-gray-600 mt-1.5 truncate">{card.agent_name}</p>
                      </div>
                    );
                  })}
                  {stageCards.length === 0 && (
                    <div
                      onClick={() => setShowAddModal(stage)}
                      className="border-2 border-dashed border-gray-800 rounded-lg py-6 flex items-center justify-center cursor-pointer hover:border-gray-700 transition-colors"
                    >
                      <Plus size={16} className="text-gray-700" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Card Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white">
                Add to {STAGE_CONFIG[showAddModal]?.label}
              </h3>
              <button onClick={() => setShowAddModal(null)} className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <input
                placeholder="Client name *"
                value={addForm.client_name}
                onChange={e => setAddForm(f => ({ ...f, client_name: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600"
              />
              <input
                placeholder="Carrier"
                value={addForm.carrier}
                onChange={e => setAddForm(f => ({ ...f, carrier: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600"
              />
              <input
                placeholder="Plan type (e.g. MAPD)"
                value={addForm.plan_type}
                onChange={e => setAddForm(f => ({ ...f, plan_type: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600"
              />
              <input
                placeholder="Plan name"
                value={addForm.plan_name}
                onChange={e => setAddForm(f => ({ ...f, plan_name: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600"
              />
              <select
                value={addForm.priority}
                onChange={e => setAddForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white"
              >
                <option value="low">Low Priority</option>
                <option value="normal">Normal Priority</option>
                <option value="high">High Priority</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowAddModal(null)}
                className="flex-1 px-4 py-2 text-sm text-gray-400 bg-gray-800 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={createCard}
                className="flex-1 px-4 py-2 text-sm font-semibold bg-amber-400 text-gray-950 rounded hover:bg-amber-300"
              >
                Add Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Detail Slide-out */}
      {selectedCard && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedCard(null)} />
          <div className="relative w-full max-w-md bg-gray-950 border-l border-gray-800 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
              <div>
                <h3 className="font-semibold text-white">{selectedCard.client_name}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                    style={{ backgroundColor: `${STAGE_CONFIG[selectedCard.stage]?.color}25`, color: STAGE_CONFIG[selectedCard.stage]?.color }}
                  >
                    {STAGE_CONFIG[selectedCard.stage]?.label}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedCard(null)} className="text-gray-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Details */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Carrier', value: selectedCard.carrier },
                  { label: 'Plan Type', value: selectedCard.plan_type },
                  { label: 'Plan Name', value: selectedCard.plan_name },
                  { label: 'Agent', value: selectedCard.agent_name },
                  { label: 'Priority', value: selectedCard.priority },
                  { label: 'Days in Stage', value: `${Math.floor(selectedCard.days_in_stage || 0)}d` },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm text-white mt-0.5">{value || '—'}</p>
                  </div>
                ))}
              </div>

              {/* Stage Actions */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Move to Stage</p>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map(s => (
                    <button
                      key={s}
                      onClick={() => { setStage(selectedCard.id, s); }}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        s === selectedCard.stage
                          ? 'border-amber-400 text-amber-400 bg-amber-400/10'
                          : 'border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-400'
                      }`}
                    >
                      {STAGE_CONFIG[s].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => advanceStage(selectedCard)}
                  disabled={selectedCard.stage === 'pending_approval' || selectedCard.stage === 'enrolled' || selectedCard.stage === 'declined'}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-amber-400 text-gray-950 rounded hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowRight size={14} /> Advance Stage
                </button>
              </div>

              {/* Notes Log */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Notes</p>
                {selectedCard.notes ? (
                  <div className="text-sm text-gray-300 whitespace-pre-wrap bg-gray-900 rounded p-3 border border-gray-800">
                    {selectedCard.notes}
                  </div>
                ) : (
                  <p className="text-xs text-gray-600">No notes yet.</p>
                )}
                <div className="flex gap-2 mt-2">
                  <input
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && addNote()}
                    className="flex-1 px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600"
                  />
                  <button
                    onClick={addNote}
                    className="px-3 py-2 text-xs font-semibold bg-gray-700 text-white rounded hover:bg-gray-600"
                  >
                    <MessageSquare size={14} />
                  </button>
                </div>
              </div>

              {/* History */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Stage History</p>
                {slideLoading ? (
                  <p className="text-xs text-gray-600">Loading...</p>
                ) : (
                  <div className="space-y-2">
                    {slideHistory.map(h => (
                      <div key={h.id} className="flex gap-2 text-xs">
                        <span className="text-gray-600 shrink-0 tabular-nums">
                          {new Date(h.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-gray-400">
                          {h.from_stage ? (
                            <><span className="text-gray-500">{STAGE_CONFIG[h.from_stage]?.label}</span> → <span className="text-amber-400">{STAGE_CONFIG[h.to_stage]?.label}</span></>
                          ) : (
                            <span className="text-amber-400">{STAGE_CONFIG[h.to_stage]?.label}</span>
                          )}
                          {h.note && ` — ${h.note}`}
                        </span>
                        <span className="text-gray-700 ml-auto shrink-0">{h.changed_by_name}</span>
                      </div>
                    ))}
                    {slideHistory.length === 0 && <p className="text-xs text-gray-700">No history.</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB 3: COMMISSION FORECAST
// ============================================================================
function CommissionForecast({ user }) {
  const [period, setPeriod] = useState('year');
  const [forecast, setForecast] = useState(null);
  const [byAgent, setByAgent] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRates, setShowRates] = useState(false);
  const [rateForm, setRateForm] = useState({ carrier: '', plan_type: 'MAPD', pmpm_amount: '' });
  const [rateSaving, setRateSaving] = useState(false);

  const isOwner = ['agency_owner', 'agency_admin', 'agency', 'fmo', 'admin', 'founder'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fRes, aRes, rRes] = await Promise.all([
        fetch(`${BASE}/api/enrollment-tracker/commission-forecast?period=${period}`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/commission-by-agent?period=${period}`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/commission-rates`, { credentials: 'include' }),
      ]);
      const [fd, ad, rd] = await Promise.all([fRes.json(), aRes.json(), rRes.json()]);
      if (fd.ok) setForecast(fd.data);
      if (ad.ok) setByAgent(ad.data);
      if (rd.ok) setRates(rd.data);
    } catch (e) {
      console.error('[CommissionForecast] load error', e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const saveRate = async () => {
    if (!rateForm.carrier || !rateForm.pmpm_amount) return;
    setRateSaving(true);
    try {
      await fetch(`${BASE}/api/enrollment-tracker/commission-rates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rateForm)
      });
      setRateForm({ carrier: '', plan_type: 'MAPD', pmpm_amount: '' });
      load();
    } finally {
      setRateSaving(false);
    }
  };

  const deleteRate = async (id) => {
    await fetch(`${BASE}/api/enrollment-tracker/commission-rates/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    load();
  };

  const fmtDollar = (n) => n >= 1000000
    ? `$${(n / 1000000).toFixed(1)}M`
    : n >= 1000
    ? `$${(n / 1000).toFixed(0)}k`
    : `$${Math.round(n)}`;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-400">Period:</span>
        {[['month', 'Current Month'], ['quarter', 'Current Quarter'], ['year', 'Full Year']].map(([val, lbl]) => (
          <button
            key={val}
            onClick={() => setPeriod(val)}
            className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
              period === val
                ? 'bg-amber-400 text-gray-950 font-semibold'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {lbl}
          </button>
        ))}
        {isOwner && (
          <button
            onClick={() => setShowRates(v => !v)}
            className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 hover:text-amber-400 transition-colors"
          >
            <Settings size={14} />
            Commission Rates
          </button>
        )}
      </div>

      {/* Revenue KPIs */}
      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard title="Total Enrollments" value={forecast?.totalEnrollments ?? '—'} icon={CheckCircle} loading={false} />
            <KpiCard
              title="Projected Revenue"
              value={forecast?.totalRevenue != null ? fmtDollar(forecast.totalRevenue) : '—'}
              icon={DollarSign}
              accent
              loading={false}
            />
            <KpiCard
              title="Last Year (same period)"
              value={forecast?.lastYearRevenue != null ? fmtDollar(forecast.lastYearRevenue) : '—'}
              icon={TrendingUp}
              loading={false}
            />
            <KpiCard
              title="YoY Change"
              value={forecast?.yoyChange != null ? `${forecast.yoyChange > 0 ? '+' : ''}${forecast.yoyChange}%` : '—'}
              icon={Activity}
              trend={forecast?.yoyChange}
              loading={false}
              accent={forecast?.yoyChange > 0}
            />
          </div>

          {/* Commission by Carrier Chart */}
          {forecast?.byCarrier?.length > 0 && (
            <Card className="p-5">
              <SectionHeader>Commission by Carrier</SectionHeader>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={forecast.byCarrier} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="carrier" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis tickFormatter={v => fmtDollar(v)} tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [fmtDollar(v), 'Revenue']}
                    contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 6 }}
                    labelStyle={{ color: '#9ca3af' }}
                    itemStyle={{ color: '#f59e0b' }}
                  />
                  <Bar dataKey="revenue" name="Revenue" radius={[4, 4, 0, 0]}>
                    {forecast.byCarrier.map((_, i) => (
                      <Cell key={i} fill={CARRIER_COLORS[i % CARRIER_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Commission by Agent */}
          {byAgent.length > 0 && (
            <Card className="p-5">
              <SectionHeader>Commission by Agent</SectionHeader>
              <div className="space-y-3">
                {byAgent.slice(0, 10).map((a, i) => {
                  const maxRev = byAgent[0]?.estimatedRevenue || 1;
                  return (
                    <div key={a.agentId}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-300 truncate max-w-[55%]">{a.agentName}</span>
                        <span className="text-gray-400 tabular-nums">
                          {fmtDollar(a.estimatedRevenue)} ({a.enrollmentCount} enrollments)
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-1.5 rounded-full"
                          style={{
                            width: `${Math.round((a.estimatedRevenue / maxRev) * 100)}%`,
                            backgroundColor: CARRIER_COLORS[i % CARRIER_COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Commission Rates Panel */}
      {showRates && isOwner && (
        <Card className="p-5">
          <SectionHeader>Commission Rate Configuration (PMPM)</SectionHeader>
          <p className="text-xs text-gray-500 mb-4">
            Set per-carrier per-member-per-month rates for revenue projections. Default: $38.33/month ($460/year).
          </p>

          {/* Add Rate */}
          <div className="flex flex-wrap gap-2 mb-5">
            <input
              placeholder="Carrier name"
              value={rateForm.carrier}
              onChange={e => setRateForm(f => ({ ...f, carrier: e.target.value }))}
              className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600 w-44"
            />
            <input
              placeholder="Plan type"
              value={rateForm.plan_type}
              onChange={e => setRateForm(f => ({ ...f, plan_type: e.target.value }))}
              className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600 w-28"
            />
            <input
              type="number"
              placeholder="$/mo"
              value={rateForm.pmpm_amount}
              onChange={e => setRateForm(f => ({ ...f, pmpm_amount: e.target.value }))}
              className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-600 w-24 tabular-nums"
            />
            <button
              onClick={saveRate}
              disabled={rateSaving}
              className="px-4 py-1.5 text-sm font-semibold bg-amber-400 text-gray-950 rounded hover:bg-amber-300 disabled:opacity-50"
            >
              {rateSaving ? 'Saving...' : 'Add Rate'}
            </button>
          </div>

          {/* Rates Table */}
          {rates.length === 0 ? (
            <p className="text-xs text-gray-600">No rates configured. Using default $38.33/month.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                    <th className="pb-2">Carrier</th>
                    <th className="pb-2">Plan Type</th>
                    <th className="pb-2 tabular-nums">PMPM</th>
                    <th className="pb-2 tabular-nums">Annual</th>
                    <th className="pb-2" />
                  </tr>
                </thead>
                <tbody>
                  {rates.map(r => (
                    <tr key={r.id} className="border-b border-gray-800/50">
                      <td className="py-2 text-white">{r.carrier}</td>
                      <td className="py-2 text-gray-400">{r.plan_type}</td>
                      <td className="py-2 text-amber-400 tabular-nums">${parseFloat(r.pmpm_amount).toFixed(2)}</td>
                      <td className="py-2 text-gray-400 tabular-nums">${parseFloat(r.annual_amount).toFixed(0)}</td>
                      <td className="py-2 text-right">
                        <button onClick={() => deleteRate(r.id)} className="text-gray-700 hover:text-red-400">
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// TAB 4: T65 PIPELINE
// ============================================================================
function T65Pipeline() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('turning65Date');
  const [sortDir, setSortDir] = useState('asc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, lRes] = await Promise.all([
        fetch(`${BASE}/api/enrollment-tracker/t65-summary`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/t65-list`, { credentials: 'include' }),
      ]);
      const [sd, ld] = await Promise.all([sRes.json(), lRes.json()]);
      if (sd.ok) setSummary(sd.data);
      if (ld.ok) setList(ld.data);
    } catch (e) {
      console.error('[T65] load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = [...list].sort((a, b) => {
    let va = a[sortBy], vb = b[sortBy];
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    return sortDir === 'asc' ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1);
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortBtn = ({ col, label }) => (
    <button
      onClick={() => toggleSort(col)}
      className={`flex items-center gap-1 hover:text-amber-400 transition-colors ${sortBy === col ? 'text-amber-400' : 'text-gray-500'}`}
    >
      {label}
      {sortBy === col && <span className="text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </button>
  );

  const maxMonth = summary?.monthly?.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  const fmtDollar = (n) => n >= 1000000
    ? `$${(n / 1000000).toFixed(1)}M`
    : n >= 1000
    ? `$${(n / 1000).toFixed(0)}k`
    : `$${Math.round(n)}`;

  return (
    <div className="space-y-6">
      {/* What is T65 callout */}
      <Card className="p-4 border-l-4 border-l-amber-400">
        <p className="text-sm text-gray-300">
          <span className="font-semibold text-amber-400">T65 Pipeline</span> — Clients turning 65 in the next 12 months
          have Initial Enrollment Periods (IEPs) and are not bound by AEP/OEP windows. Each has a 7-month IEP
          (3 months before their birthday month through 3 months after).
        </p>
      </Card>

      {/* Count Cards */}
      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Next 90 Days', ...summary?.totals?.next90 },
              { label: 'Next 6 Months', ...summary?.totals?.next180 },
              { label: 'Next 12 Months', ...summary?.totals?.next365 },
            ].map(({ label, count, estimatedRevenue }) => (
              <Card key={label} className="p-5">
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                <p className="text-3xl font-bold text-white tabular-nums">{count ?? 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Est. {fmtDollar(estimatedRevenue ?? 0)} at 52% conversion
                </p>
              </Card>
            ))}
          </div>

          {/* 12-Month Timeline */}
          {summary?.monthly?.length > 0 && (
            <Card className="p-5">
              <SectionHeader>T65 Pipeline — Monthly View</SectionHeader>
              <div className="overflow-x-auto">
                <div className="flex gap-3 min-w-max pb-2">
                  {summary.monthly.map(m => (
                    <div key={m.month} className="flex flex-col items-center w-20">
                      <div className="relative w-full h-28 flex items-end justify-center mb-2">
                        <div
                          className="w-14 rounded-t-md bg-amber-400/80 transition-all"
                          style={{ height: `${m.count > 0 ? Math.max(8, Math.round((m.count / maxMonth) * 100)) : 4}%` }}
                        />
                        {m.count > 0 && (
                          <span className="absolute bottom-full mb-1 text-xs font-bold text-amber-400 tabular-nums">
                            {m.count}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-500 text-center">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* T65 Client Table */}
          <Card className="p-5">
            <SectionHeader>T65 Client List ({list.length})</SectionHeader>
            {list.length === 0 ? (
              <EmptyState message="No clients turning 65 in the next 12 months" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs border-b border-gray-800">
                      <th className="pb-2 pr-4"><SortBtn col="clientMasked" label="Client" /></th>
                      <th className="pb-2 pr-4"><SortBtn col="birthdayMonth" label="Turns 65" /></th>
                      <th className="pb-2 pr-4 text-gray-500">IEP Window</th>
                      <th className="pb-2 pr-4 text-gray-500">ZIP</th>
                      <th className="pb-2 pr-4"><SortBtn col="currentCarrier" label="Carrier" /></th>
                      <th className="pb-2 pr-4"><SortBtn col="agentName" label="Agent" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map(c => (
                      <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="py-2 pr-4 font-medium text-white">{c.clientMasked}</td>
                        <td className="py-2 pr-4 text-amber-400 tabular-nums">
                          {new Date(c.turning65Date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-2 pr-4 text-gray-500 tabular-nums text-xs">
                          {new Date(c.iepStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {new Date(c.iepEnd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="py-2 pr-4 text-gray-400 tabular-nums">{c.zip || '—'}</td>
                        <td className="py-2 pr-4 text-gray-300">{c.currentCarrier}</td>
                        <td className="py-2 pr-4 text-gray-400">{c.agentName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ============================================================================
// TAB 5: CARRIER INTELLIGENCE
// ============================================================================
function CarrierIntelligence() {
  const [dist, setDist] = useState([]);
  const [perf, setPerf] = useState([]);
  const [trend, setTrend] = useState([]);
  const [trendCarriers, setTrendCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, pRes, tRes] = await Promise.all([
        fetch(`${BASE}/api/enrollment-tracker/carrier-dist`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/carrier-performance`, { credentials: 'include' }),
        fetch(`${BASE}/api/enrollment-tracker/carrier-trend`, { credentials: 'include' }),
      ]);
      const [dd, pd, td] = await Promise.all([dRes.json(), pRes.json(), tRes.json()]);
      if (dd.ok) setDist(dd.data);
      if (pd.ok) { setPerf(pd.data); if (pd.data.length >= 2) { setCompareA(pd.data[0]?.carrier || ''); setCompareB(pd.data[1]?.carrier || ''); } }
      if (td.ok) { setTrend(td.data); setTrendCarriers(td.carriers || []); }
    } catch (e) {
      console.error('[CarrierIntelligence] load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const carrierA = perf.find(p => p.carrier === compareA);
  const carrierB = perf.find(p => p.carrier === compareB);
  const topTrendCarriers = trendCarriers.slice(0, 5);

  const TREND_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444'];

  const fmtPct = (n) => `${n ?? 0}%`;

  return (
    <div className="space-y-6">
      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Donut Chart */}
            <Card className="p-5">
              <SectionHeader>Market Share — Agency Book</SectionHeader>
              {dist.length === 0 ? <EmptyState message="No enrollment data" /> : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie
                        data={dist}
                        dataKey="count"
                        nameKey="carrier"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                      >
                        {dist.map((_, i) => (
                          <Cell key={i} fill={CARRIER_COLORS[i % CARRIER_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, n) => [`${v} clients`, n]}
                        contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 6 }}
                        itemStyle={{ color: '#f59e0b' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5 flex-1 min-w-0">
                    {dist.map((c, i) => (
                      <div key={c.carrier} className="flex items-center gap-2 text-xs">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CARRIER_COLORS[i % CARRIER_COLORS.length] }} />
                        <span className="text-gray-300 truncate flex-1">{c.carrier}</span>
                        <span className="text-gray-500 tabular-nums shrink-0">{c.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Carrier Performance Table */}
            <Card className="p-5">
              <SectionHeader>Carrier Performance</SectionHeader>
              {perf.length === 0 ? <EmptyState message="No data" /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-800">
                        <th className="pb-2 pr-3">Carrier</th>
                        <th className="pb-2 pr-3 tabular-nums">Active</th>
                        <th className="pb-2 pr-3 tabular-nums">Churn</th>
                        <th className="pb-2 pr-3 tabular-nums">Churn %</th>
                        <th className="pb-2 tabular-nums">Retention</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perf.map(c => (
                        <tr key={c.carrier} className="border-b border-gray-800/50">
                          <td className="py-2 pr-3 text-white font-medium truncate max-w-[100px]">{c.carrier}</td>
                          <td className="py-2 pr-3 text-gray-300 tabular-nums">{c.activeClients}</td>
                          <td className="py-2 pr-3 text-gray-400 tabular-nums">{c.churnedClients}</td>
                          <td className="py-2 pr-3 tabular-nums">
                            <span className={c.churnRate > 20 ? 'text-red-400' : c.churnRate > 10 ? 'text-amber-400' : 'text-green-400'}>
                              {fmtPct(c.churnRate)}
                            </span>
                          </td>
                          <td className="py-2 tabular-nums">
                            <span className={c.retentionRate >= 85 ? 'text-green-400' : c.retentionRate >= 70 ? 'text-amber-400' : 'text-red-400'}>
                              {fmtPct(c.retentionRate)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* 12-Month Trend */}
          <Card className="p-5">
            <SectionHeader>Carrier Enrollment Trend — Last 12 Months</SectionHeader>
            {trend.length === 0 ? <EmptyState message="No trend data" /> : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend} margin={{ top: 5, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis
                    dataKey="month"
                    tickFormatter={m => m.slice(5)}
                    tick={{ fill: '#6b7280', fontSize: 11 }}
                  />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 6 }}
                    labelStyle={{ color: '#9ca3af' }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11, color: '#9ca3af' }}
                    iconType="circle"
                  />
                  {topTrendCarriers.map((carrier, i) => (
                    <Line
                      key={carrier}
                      type="monotone"
                      dataKey={carrier}
                      stroke={TREND_COLORS[i % TREND_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Carrier Comparison */}
          {perf.length >= 2 && (
            <Card className="p-5">
              <SectionHeader>Side-by-Side Carrier Comparison</SectionHeader>
              <div className="flex gap-4 mb-5">
                <select
                  value={compareA}
                  onChange={e => setCompareA(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-white"
                >
                  {perf.map(p => <option key={p.carrier} value={p.carrier}>{p.carrier}</option>)}
                </select>
                <span className="text-gray-500 self-center">vs.</span>
                <select
                  value={compareB}
                  onChange={e => setCompareB(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded text-white"
                >
                  {perf.map(p => <option key={p.carrier} value={p.carrier}>{p.carrier}</option>)}
                </select>
              </div>

              {carrierA && carrierB && (
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Active Clients', keyA: 'activeClients', keyB: 'activeClients', fmt: n => n },
                    { label: 'Churned Clients', keyA: 'churnedClients', keyB: 'churnedClients', fmt: n => n },
                    { label: 'Churn Rate', keyA: 'churnRate', keyB: 'churnRate', fmt: n => `${n}%` },
                    { label: 'Retention Rate', keyA: 'retentionRate', keyB: 'retentionRate', fmt: n => `${n}%` },
                  ].map(({ label, keyA, keyB, fmt }) => (
                    <div key={label} className="bg-gray-800/50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-2">{label}</p>
                      <div className="flex items-end gap-4">
                        <div>
                          <p className="text-[11px] text-amber-400 mb-0.5 truncate">{compareA}</p>
                          <p className="text-xl font-bold text-white tabular-nums">{fmt(carrierA[keyA])}</p>
                        </div>
                        <div>
                          <p className="text-[11px] text-blue-400 mb-0.5 truncate">{compareB}</p>
                          <p className="text-xl font-bold text-white tabular-nums">{fmt(carrierB[keyB])}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================
const TABS = [
  { key: 'command', label: 'Command Center', icon: Zap },
  { key: 'pipeline', label: 'Pipeline', icon: Activity },
  { key: 'commission', label: 'Commission Forecast', icon: DollarSign },
  { key: 't65', label: 'T65 Pipeline', icon: Users },
  { key: 'carriers', label: 'Carrier Intelligence', icon: BarChart3 },
];

export default function AgencyEnrollmentTracker() {
  const [activeTab, setActiveTab] = useState('command');
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.user) setUser(d.user); })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Agency Enrollment Tracker</h1>
          <p className="text-sm text-gray-500 mt-1">
            AEP/OEP velocity, pipeline management, commission forecasting, T65 opportunities, and carrier intelligence.
          </p>
        </div>

        {/* Tab Bar */}
        <div className="flex overflow-x-auto gap-1 mb-6 pb-1 border-b border-gray-800">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors -mb-px ${
                activeTab === key
                  ? 'border-amber-400 text-amber-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-700'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'command'    && <CommandCenter user={user} />}
          {activeTab === 'pipeline'   && <PipelineKanban user={user} />}
          {activeTab === 'commission' && <CommissionForecast user={user} />}
          {activeTab === 't65'        && <T65Pipeline />}
          {activeTab === 'carriers'   && <CarrierIntelligence />}
        </div>
      </div>
    </div>
  );
}
